/**
 * Stock en venta / listado: total de depósitos + reservado en remitos abiertos.
 * La venta no se bloquea por stock ni por reserva.
 * Run: npx tsx src/lib/ventaStockDisponible.test.ts
 */
export type ProductoStockLike = {
  codigo?: string | null;
  stockDisponible?: number | string | null;
  stock_disponible?: number | string | null;
  stockActual?: number | string | null;
  stock_actual?: number | string | null;
  stockReservado?: number | string | null;
  stock_reservado?: number | string | null;
  stockPedido?: number | string | null;
  stock_pedido?: number | string | null;
  stockControlado?: boolean;
  stock_controlado?: boolean;
  esPersonalizado?: boolean;
  es_personalizado?: boolean;
  nombre?: string;
};

function toQty(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function formatStockQty(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  const text = String(n);
  if (text.includes("e") || text.includes("E")) return n.toFixed(3).replace(/\.?0+$/, "");
  return text;
}

/** Visible: `1000`, `1000 (12)`, `-100 (100)`. Sin paréntesis si reservado = 0. */
export function formatStockTotalLabel(total: number, reserved: number): string {
  const label = formatStockQty(total);
  if (!(reserved > 0)) return label;
  return `${label} (${formatStockQty(reserved)})`;
}

export function stockTotalEsPositivo(total: number): boolean {
  return total > 0;
}

export function productoCodigoLabel(p: ProductoStockLike | string): string {
  if (typeof p === "string") {
    const trimmed = p.trim();
    return trimmed || "producto";
  }
  const codigo = (p.codigo ?? "").trim();
  return codigo || "producto";
}

export function productoEsPersonalizado(p: ProductoStockLike): boolean {
  return p.esPersonalizado === true || p.es_personalizado === true;
}

export function productoStockActual(p: ProductoStockLike): number {
  return toQty(p.stockActual ?? p.stock_actual) ?? 0;
}

/** Qty en remitos pendiente/parcial no entregada. */
/** Qty en pedidos a proveedor no recibidos. 0 si no vino el campo. */
export function productoStockPedido(p: ProductoStockLike): number {
  const explicit = toQty(p.stockPedido ?? p.stock_pedido);
  if (explicit !== null && explicit > 0) return explicit;
  return 0;
}

export function productoStockReservado(p: ProductoStockLike): number {
  const explicit = toQty(p.stockReservado ?? p.stock_reservado);
  if (explicit !== null) return explicit > 0 ? explicit : 0;
  const actual = toQty(p.stockActual ?? p.stock_actual);
  const disp = toQty(p.stockDisponible ?? p.stock_disponible);
  if (actual != null && disp != null && actual > disp) return actual - disp;
  return 0;
}

export function productoStockControlado(p: ProductoStockLike): boolean {
  if (p.stockControlado === true || p.stock_controlado === true) return true;
  if (p.stockControlado === false || p.stock_controlado === false) return false;
  return (toQty(p.stockActual ?? p.stock_actual) ?? 0) !== 0
    || productoStockReservado(p) > 0;
}

/** Compat: ya no se usa para bloquear. Prefiere stock actual. */
export function productoStockDisponible(p: ProductoStockLike): number {
  const explicit = toQty(p.stockDisponible ?? p.stock_disponible);
  if (explicit !== null) return explicit;
  return productoStockActual(p);
}

/** Hard-block de venta por stock/reserva: nunca. */
export function ventaBloqueaPorStock(
  _qty?: number,
  _available?: number,
  _reservadoEnRemito?: boolean
): false {
  return false;
}

export function mensajeBloqueoStockVenta(
  _codigo: string,
  _qty: number,
  _available: number,
  _reservadoEnRemito = false
): string | null {
  return null;
}
